import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { LoanCalculationChart } from "../LoanCalculator/LoanCalculationChart";
import {
  Grid,
  GridItem,
  SimpleGrid,
  Input,
  FormControl,
  FormLabel,
  Select,
  Radio,
  RadioGroup,
  Stack,
  Box,
  Tooltip,
  Text,
} from "@chakra-ui/react";
import { formatCurrency } from "../helpers/number.helpers";
import {
  LoanTypes,
  useCalculatedLoan,
  useLoanCalculatorController,
} from "../LoanCalculator/LoanCalculator.hooks";
import { LoanCalculationTable } from "../LoanCalculator/LoanCalculationTable";
import { Button, ButtonVariants } from "../Button/Button";
import { Icon } from "../icons/Icon";
import { Heading } from "../Heading/Heading";



const TooltipIcon = ({ tooltip }) => {
const [isLabelOpen, setIsLabelOpen] = useState(false);

  return (
  <Box position="relative" width="4" height="4">
    <Icon icon="info" size="sm" display="block"  />
    <Tooltip hasArrow placement="top" label={<Box whiteSpace="pre-wrap">{tooltip}</Box>} isOpen={isLabelOpen} >
      <Box width="4" height="4" position="absolute" top="0" onMouseEnter={() => setIsLabelOpen(true)}
                onMouseLeave={() => setIsLabelOpen(false)}
                onClick={() => setIsLabelOpen(true)} />
    </Tooltip>
  </Box>
)};
TooltipIcon.propTypes = {
  tooltip: PropTypes.string,
};

const LabelWithTooltip = ({ label, tooltip }) => (
  
  <Box display="flex" alignItems="center" marginBottom="2">
    <FormLabel marginBottom="0">{label}:</FormLabel>
    <TooltipIcon {...{ tooltip }} />
  </Box>
);

LabelWithTooltip.propTypes = {
  label: PropTypes.string,
  tooltip: PropTypes.string,
};


export const AdvancedLoanCalculatorBlock = ({
  title,
  language,
  initialLoanAmount = 50000,
  initialLoanYears: initialLoanMonths = 60,
  initialInterestRate = 7.95,
  initialNumberOfYearlyPayments = 12,
  minInterestRate = 0.01,
  maxInterestRate = 99.99,
  establishmentFee = 0,
  content,
  loanTypeLabel,
  loanTypeTooltip,
  loanAmountLabel,
  loanAmountTooltip,
  loanYearsLabel,
  loanYearsTooltip,
  yearlyPaymentsLabel,
  yearlyPaymentsTooltip,
  interestRateLabel,
  interestRateTooltip,
  paymentFeeLabel,
  paymentFeeTooltip,
  establishmentFeeLabel,
  establishmentFeeTooltip,
  installmentLabel,
  installmentPaymentLabel,
  interestCostLabel,
  feesLabel,
  amortizationLabel,
  remainingLabel,
  yourLoanCostLabel,
  monthlyFee,
  totalCostLabel,
  paymentPlanLabel,
  viewPaymentPlanLabel,
  hidePaymentPlanLabel,
  monthly,
  quarterly,
  yearly,
  annuityLoanLabel,
  serialLoanLabel,
  monthLabel,
  yearLabel,
  quarterLabel,
  initialPaymentFee = (language === "sv" ? 29 : language === "no" ? 45 : language === "dk" ? 0 : null),
}) => {
  const langSpecificKRsuffix = language === "dk" ? "." : "";
  const numberSeparator = language === "dk" ? "." : " ";
  const handleNumberChange = (value) => {
    const string = String(value);
    const stripped = string.replace(/\D+/g, "");

    const output = stripped;
    return formatCurrency(output, numberSeparator);
  };
  const [loanAmount, setLoanAmount] = useState(
    handleNumberChange(initialLoanAmount)
  );
  const [loanMonths, setLoanMonths] = useState(initialLoanMonths);
  const [paymentFee, setPaymentFee] = useState(initialPaymentFee);
  const [_establishmentFee, setEstablishmentFee] = useState(establishmentFee);

  const viewPlanRef = useRef(null);
  const [isPlanVisible, setIsPlanVisible] = useState(false);
  const loanCalculator = useLoanCalculatorController({
    initialLoanType: LoanTypes.Annuity,
    initialLoanAmount,
    initialLoanYears: initialLoanMonths,
    initialInterestRate,
    initialPaymentFee,
    initialEstablishmentFee: establishmentFee,
    initialNumberOfYearlyPayments,
    minInterestRate,
    maxInterestRate,
    //See LoanCalcualtor.config:
    version: language.toUpperCase(),
  });

  const calculatedLoan = useCalculatedLoan({
    loanType: loanCalculator.loanType,
    amount: loanCalculator.amount,
    months: loanCalculator.years,
    interestRate: loanCalculator.interestRate,
    establishmentFee: loanCalculator.establishmentFee,
    paymentFee: loanCalculator.paymentFee,
    numberOfYearlyPayments: loanCalculator.numberOfYearlyPayments,
  });



  return (
    <>
      <Grid
        width="100%"
        templateColumns="repeat(12, 1fr)"
        gap={{ base: 4, lg: 8 }}
      >
        <GridItem colSpan={{ base: 12, lg: 12 }}>
          <FormControl defaultValue={loanCalculator.loanType}>
            <RadioGroup
              value={loanCalculator.loanType}
              onChange={(e) => loanCalculator.setLoanType(e)}
            >
              <Stack spacing={5} direction="row">
                <Radio value={LoanTypes.Annuity}>{annuityLoanLabel}</Radio>
              </Stack>
            </RadioGroup>
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 12, lg: 4 }}>
          <FormControl id="loanAmount" position="relative">
            <LabelWithTooltip
              label={loanAmountLabel}
              tooltip={loanAmountTooltip}
            />
            <Box position="relative">
              <Input
                type="text"
                defaultValue={loanCalculator.amount}
                value={loanAmount}
                onChange={(e) =>
                  setLoanAmount(handleNumberChange(e.currentTarget.value))
                }
                onBlur={(e) =>
                  loanCalculator.setAmount(
                    Number(e.currentTarget.value.replace(/\D+/g, ""))
                  )
                }
                textAlign="right"
                paddingRight={language === "dk" ? "7" : "6"}
              />
              <Box
                height="100%"
                position="absolute"
                right="0"
                top="0"
                display="flex"
                alignItems="center"
                marginRight="2"
              >
                kr{langSpecificKRsuffix}
              </Box>
            </Box>
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 12, lg: 4 }}>
          <SimpleGrid columns={1} spacing={4}>
            <FormControl id="loanYears">
              <LabelWithTooltip
                label={loanYearsLabel}
                tooltip={loanYearsTooltip}
              />
              <Box position="relative">
                <Input
                  type="text"
                  defaultValue={loanCalculator.years}
                  value={loanMonths}
                  onChange={(e) =>
                    setLoanMonths(handleNumberChange(e.currentTarget.value))
                  }
                  onBlur={(e) =>
                    loanCalculator.setYears(
                      Number(e.currentTarget.value.replace(/\D+/g, "")) | 0
                    )
                  }
                  textAlign="right"
                  paddingRight="10"
                />
                <Box
                  height="100%"
                  position="absolute"
                  right="0"
                  top="0"
                  display="flex"
                  alignItems="center"
                  marginRight="1"
                >
                  {language === "dk" ? "mdr." : "mån"}
                </Box>
              </Box>
            </FormControl>
            {/* <FormControl id="yearlyPayments">
              <LabelWithTooltip
                label={yearlyPaymentsLabel}
                tooltip={yearlyPaymentsTooltip}
              />
              <Select
                defaultValue={initialNumberOfYearlyPayments}
                onChange={(e) =>
                  loanCalculator.setNumberOfYearlyPayments(
                    Number(e.currentTarget.value)
                  )
                }
              >
                <option value="12">{monthly}</option>
                <option value="4">{quarterly}</option>
                <option value="1">{yearly}</option>
              </Select>
            </FormControl> */}
          </SimpleGrid>
        </GridItem>
        <GridItem colSpan={{ base: 12, lg: 4 }}>
          <FormControl id="interestRate">
            <LabelWithTooltip
              label={interestRateLabel}
              tooltip={interestRateTooltip}
            />
            <Box position="relative">
              <Input
                type="number"
                defaultValue={loanCalculator.interestRate}
                onBlur={(e) =>
                  loanCalculator.setInterestRate(Number(e.currentTarget.value))
                }
                textAlign="right"
                paddingRight="6"
              />
              <Box
                height="100%"
                position="absolute"
                right="0"
                top="0"
                display="flex"
                alignItems="center"
                marginRight="1"
              >
                %
              </Box>
            </Box>
          </FormControl>
        </GridItem>
        {language !== "dk" ? (
          <GridItem colSpan={{ base: 12, lg: 4 }}>
            <FormControl id="paymentFee">
              <LabelWithTooltip
                label={paymentFeeLabel}
                tooltip={paymentFeeTooltip}
              />
              <Box position="relative">
                <Input
                  type="text"
                  defaultValue={loanCalculator.paymentFee}
                  value={paymentFee}
                  onChange={(e) =>
                    setPaymentFee(handleNumberChange(e.currentTarget.value))
                  }
                  onBlur={(e) =>
                    loanCalculator.setPaymentFee(
                      Number(e.currentTarget.value.replace(/\D+/g, ""))
                    )
                  }
                  textAlign="right"
                  paddingRight="6"
                />
                <Box
                  height="100%"
                  position="absolute"
                  right="0"
                  top="0"
                  display="flex"
                  alignItems="center"
                  marginRight="2"
                >
                  kr{langSpecificKRsuffix}
                </Box>
              </Box>
            </FormControl>
          </GridItem>
        ) : (
          ""
        )}
        {/* <GridItem colSpan={{ base: 12, lg: 4 }}>
          <FormControl id="establishmentFee">
            <LabelWithTooltip
              label={establishmentFeeLabel}
              tooltip={establishmentFeeTooltip}
            />
            <Box position="relative">
              <Input
                type="text"
                defaultValue={loanCalculator.establishmentFee}
                value={_establishmentFee}
                onChange={(e) =>
                  setEstablishmentFee(handleNumberChange(e.currentTarget.value))
                }
                onBlur={(e) =>
                  loanCalculator.setEstablishmentFee(
                    Number(e.currentTarget.value.replace(/\D+/g, ""))
                  )
                }
                textAlign="right"
                paddingRight="8"
              />
              <Box
                height="100%"
                position="absolute"
                right="0"
                top="0"
                display="flex"
                alignItems="center"
                paddingRight="4"
              >
                kr{langSpecificKRsuffix}
              </Box>
            </Box>
          </FormControl>
        </GridItem> */}
        <GridItem colSpan={12}>
          <Heading as="h3" size="3" marginTop={{ base: 0, lg: 6 }}>
            {yourLoanCostLabel}:
          </Heading>
        </GridItem>
        <GridItem colSpan={{ base: 12, lg: 4 }}>
          <Heading as="p">{monthlyFee}:</Heading>
          <Heading
            as="span"
            size={{ base: 3, lg: 2 }}
            marginY={{ base: 2, lg: 6 }}
          >
            {/* {calculatedLoan.calculatedMonthlyFee.toString().replace(".", ",")}{" "}
            kr */}
            {formatCurrency(
              calculatedLoan.calculatedMonthlyFee,
              numberSeparator
            )}{" "}
            kr
            {language === "dk" && "."}
          </Heading>
        </GridItem>
        <GridItem colSpan={{ base: 12, lg: 4 }}>
          <Heading as="p">{interestCostLabel}:</Heading>
          <Heading
            as="span"
            size={{ base: 3, lg: 2 }}
            marginY={{ base: 2, lg: 6 }}
          >
            {formatCurrency(calculatedLoan.totalCost, numberSeparator)} kr
            {language === "dk" && "."}
          </Heading>
        </GridItem>

        <GridItem colSpan={{ base: 12, lg: 4 }}>
          <Heading as="p">{totalCostLabel}:</Heading>
          <Heading
            as="span"
            size={{ base: 3, lg: 2 }}
            marginY={{ base: 2, lg: 6 }}
          >
            {formatCurrency(calculatedLoan.totalAmount, numberSeparator)} kr
            {language === "dk" && "."}
          </Heading>
        </GridItem>
      </Grid>
      <Box
        paddingY="12"
        display="flex"
        justifyContent="center"
        ref={viewPlanRef}
      >
        {/* <Button
          title={isPlanVisible ? hidePaymentPlanLabel : viewPaymentPlanLabel}
          isButton
          variant={ButtonVariants.secondary}
          width="auto"
          paddingX="8"
          onClick={() => {
            if (!isPlanVisible && viewPlanRef.current) {
              window.scrollTo({
                top:
                  window.scrollY +
                  viewPlanRef.current.getBoundingClientRect().top,
                behavior: "smooth",
              });
            }
            setIsPlanVisible(!isPlanVisible);
          }}
        /> */}
      </Box>
      {isPlanVisible && (
        <>
          <Heading as="h3" size="2" marginBottom="8">
            {paymentPlanLabel}
          </Heading>
          <LoanCalculationChart
            language={language}
            payments={calculatedLoan.payments}
            {...{ remainingLabel }}
          />
          <LoanCalculationTable
            payments={calculatedLoan.payments}
            numberOfYearlyPayments={loanCalculator.numberOfYearlyPayments}
            {...{
              installmentLabel,
              installmentPaymentLabel,
              interestCostLabel,
              feesLabel,
              amortizationLabel,
              remainingLabel,
              monthLabel,
              yearLabel,
              quarterLabel,
              numberSeparator,
              language,
            }}
          />
        </>
      )}
    </>
  );
};

AdvancedLoanCalculatorBlock.propTypes = {
  title: PropTypes.string,
  initialLoanAmount: PropTypes.number,
  initialLoanYears: PropTypes.number,
  initialInterestRate: PropTypes.number,
  initialNumberOfYearlyPayments: PropTypes.number,
  minInterestRate: PropTypes.number,
  maxInterestRate: PropTypes.number,
  establishmentFee: PropTypes.number,
  initialPaymentFee: PropTypes.number,
  content: PropTypes.string,
  loanTypeLabel: PropTypes.string,
  loanTypeTooltip: PropTypes.string,
  loanAmountLabel: PropTypes.string,
  loanAmountTooltip: PropTypes.string,
  loanYearsLabel: PropTypes.string,
  loanYearsTooltip: PropTypes.string,
  yearlyPaymentsLabel: PropTypes.string,
  yearlyPaymentsTooltip: PropTypes.string,
  interestRateLabel: PropTypes.string,
  interestRateTooltip: PropTypes.string,
  paymentFeeLabel: PropTypes.string,
  paymentFeeTooltip: PropTypes.string,
  establishmentFeeLabel: PropTypes.string,
  establishmentFeeTooltip: PropTypes.string,
  installmentLabel: PropTypes.string,
  installmentPaymentLabel: PropTypes.string,
  interestCostLabel: PropTypes.string,
  feesLabel: PropTypes.string,
  amortizationLabel: PropTypes.string,
  remainingLabel: PropTypes.string,
  yourLoanCostLabel: PropTypes.string,
  monthlyFee: PropTypes.string,
  totalCostLabel: PropTypes.string,
  paymentPlanLabel: PropTypes.string,
  viewPaymentPlanLabel: PropTypes.string,
  hidePaymentPlanLabel: PropTypes.string,
  language: PropTypes.oneOf(['sv','dk','no'])
};
